import { StyleSheet, View, FlatList, ScrollView, TouchableOpacity } from 'react-native';
import Button from '../atoms/Button';
import Text from '../typography/Text';
import { AssetSvg } from '../../assets/svg';
import { colors } from '../../theme/colors';
import { MINIMUM_QUESTION_HEIGHT, QUESTION_WIDTH } from '../../theme/scaling';
import { noop } from '../../utils/flowControl';
import LinkText from '../atoms/LinkText';
import { useI18nContext } from '../../i18n/i18n-react';
import { ReactElement, useState } from 'react';
import { ModalCard } from '../modals';
import IconButton from '../atoms/IconButton';
import { useTheme } from '../../theme';
import { countRange } from '../../utils/collections';
import PngImage from '../../utils/pngImage';

export type ResultsArray = Array<{
  /** Number of stars (0 to 3) awarded for this question. */
  stars: number;
}>;

type QuizResults = {
  quizName: string;
  /**
   * Results of the quiz.
   *
   * Important note: if these results are from a practice quiz, this should be the best results across all practice
   * sessions, not necessarily the results of the practice quiz session which just finished. The are shown by silver
   * stars.
   */
  results: ResultsArray;
  /**
   * If present, we're showing results of a practice quiz session, and this object contains information about that.
   * If absent, we're showing results of a real quiz session.
   */
  practiceInfo?: {
    /**
     * Results of the original real quiz session that we're practicing from. These are shown by gold stars.
     */
    originalResults: ResultsArray;
    // If we ever need to know the actual questions that were attempted in the latest practice session, we could add
    // a property here.
  };
};

/**
 * Screen showing the number of stars achieved on each question of a quiz.
 * Includes return, exit, and home buttos for navigation.
 */
export default function Results({
  quizName,
  results,
  practiceInfo,
  onTryAgainClicked,
  onPracticeClicked,
  onExitClicked = noop,
  onHomeClicked
}: QuizResults & {
  /** Callback for when the try again button is clicked. If absent, the try again button will not be shown. */
  onTryAgainClicked?: () => void;
  /**
   * Callback for the user attempting to practice a single question, or the whole quiz. If absent, practice mode is
   * not offered.
   *
   * Note that if we're showing the results for a session which attained full marks, we don't offer practicing the
   * whole quiz either way - we only offer practicing individual questions.
   */
  onPracticeClicked?: (
    /**
     * The index of the question to retry. If this is absent or undefined, the user clicked the large generic practice
     * button corresponding to the whole quiz.
     */
    questionIndex?: number
  ) => void;
  /** Callback for when the close button is clicked. */
  onExitClicked?: () => void;
  /** Callback for when the back-to-home button is clocked. If absent, the try again button isn't there. */
  onHomeClicked?: () => void;
}) {
  const translate = useI18nContext().LL;
  const theme = useTheme();

  // Transform results to contain all the information we need for display
  const resultsWithIds = results.map((question, idx) => {
    return {
      questionNumber: idx + 1,
      goldStars: practiceInfo ? practiceInfo.originalResults[idx].stars : question.stars,
      // Silver stars are only used in practice mode. Their minimum is equal to the number of gold stars, but if this
      // is the results of a practice they might have earned more.
      silverStars: question.stars
    };
  });

  // Get stars total length and total score
  const goldStarsTotalScore = resultsWithIds.reduce((acc, o) => acc + o.goldStars, 0);
  const silverStarsTotalScore = resultsWithIds.reduce((acc, o) => acc + o.silverStars, 0);
  const starsTotalLength = resultsWithIds.length * 3;
  const isFullMarks = goldStarsTotalScore === starsTotalLength;

  const [modalToShow, setModalToShow] = useState<
    | { type: 'quiz' }
    | {
        type: 'question';
        /** 0-indexed */
        questionIndex: number;
      }
    | undefined
  >(undefined);

  const renderModal = () => {
    switch (modalToShow?.type) {
      case 'question':
        // Modal for practicing an individual question
        return (
          onPracticeClicked && (
            <ModalCard
              onDismiss={() => setModalToShow(undefined)}
              containerStyle={styles.modalContainer}
            >
              {/* Close button */}
              <IconButton
                iconWidth={23}
                onPress={() => setModalToShow(undefined)}
                style={styles.iconClose}
                baseColor={theme.colors.surface}
              >
                <AssetSvg name="Close" />
              </IconButton>

              <Text variant="WRN700">
                {translate.quiz.modals.retryQuestionX(modalToShow.questionIndex + 1)}
              </Text>

              <Text style={{ textAlign: 'center', fontSize: 32 }} variant="WRN400">
                {translate.quiz.modals.practiceModeEarnSilverStars()}
              </Text>
              <View
                style={{
                  alignItems: 'flex-end',
                  columnGap: 40,
                  marginTop: 32,
                  flexDirection: 'row'
                }}
                testID="quiz_try_question_again_modal"
              >
                <Button
                  testId="PRACTICE_QUESTION_CANCEL_BUTTON"
                  baseColor={theme.colors.background}
                  onPress={() => setModalToShow(undefined)}
                  style={{ width: 270, height: 96 }}
                >
                  {translate.misc.Cancel()}
                </Button>

                <Button
                  testId="PRACTICE_QUESTION_OK_BUTTON"
                  baseColor={theme.colors.secondaryContainer}
                  onPress={() => {
                    onPracticeClicked(modalToShow.questionIndex);
                    setModalToShow(undefined);
                  }}
                  style={{ width: 270, height: 96 }}
                >
                  {translate.misc.OK()}
                </Button>
              </View>
            </ModalCard>
          )
        );
      case 'quiz':
        // Modal for trying again or practicing the whole quiz
        return onTryAgainClicked && onPracticeClicked && !isFullMarks ? (
          <ModalCard
            onDismiss={() => setModalToShow(undefined)}
            containerStyle={styles.modalContainer}
          >
            {/* Close button */}
            <IconButton
              iconWidth={23}
              onPress={() => setModalToShow(undefined)}
              style={styles.iconClose}
              baseColor={theme.colors.surface}
            >
              <AssetSvg name="Close" />
            </IconButton>

            <Text variant="WRN700">{translate.quiz.modals.practiceOrRetry()}</Text>
            <View
              style={{
                alignItems: 'flex-end',
                rowGap: 20,
                marginTop: 32
              }}
              testID="quiz_try_quiz_again_modal"
            >
              <Button
                baseColor={theme.colors.background}
                testId="PRACTICE_QUIZ_MODAL_BUTTON"
                onPress={() => {
                  onPracticeClicked();
                  setModalToShow(undefined);
                }}
                style={{ width: 540, height: 96 }}
              >
                {translate.quiz.modals.practice()}
              </Button>
              <Button
                baseColor={theme.colors.secondaryContainer}
                testId="RETRY_QUIZ_BUTTON"
                onPress={() => {
                  onTryAgainClicked();
                  setModalToShow(undefined);
                }}
                style={{ width: 540, height: 96 }}
              >
                {practiceInfo
                  ? translate.quiz.modals.tryForGoldStars()
                  : translate.quiz.modals.retryQuiz()}
              </Button>
            </View>
          </ModalCard>
        ) : null;
      default:
        break;
    }
  };

  // Render ratings
  const renderRatings = () => {
    const data = resultsWithIds;

    return (
      <FlatList
        data={data}
        scrollEnabled={true}
        renderItem={({ item, index }) => {
          const stars: ReactElement[] = [];
          const goldStarsToShow = item.goldStars;
          const silverStarsToShow = item.silverStars - item.goldStars;
          const greyStarsToShow = 3 - silverStarsToShow - goldStarsToShow;

          countRange(goldStarsToShow).forEach(num =>
            stars.push(
              <View style={{ paddingLeft: 8 }} key={`star-${num}`}>
                <AssetSvg name={'Star'} height={38.2} width={40} />
              </View>
            )
          );

          countRange(silverStarsToShow).forEach(num =>
            stars.push(
              <View style={{ paddingLeft: 8 }} key={`star_silver-${num}`}>
                <AssetSvg name={'StarSilver'} height={38.2} width={40} />
              </View>
            )
          );

          countRange(greyStarsToShow).forEach(num =>
            stars.push(
              <View style={{ paddingLeft: 8 }} key={`star_transparent-${num}`}>
                <AssetSvg name={'GreyStar'} height={38.2} width={40} />
              </View>
            )
          );

          return (
            <View style={styles.rating}>
              <View style={styles.questionLabel}>
                <Text variant="WRN700" style={styles.ratingText}>
                  Q{item.questionNumber}
                </Text>
              </View>
              <View style={styles.ratingIcons}>{stars.map(star => star)}</View>
              {onPracticeClicked ? (
                // We have a practice callback, so show the button to practice the question (opens a modal)
                <TouchableOpacity
                  testID={`PRACTICE_QUESTION_BUTTON_${index}`}
                  onPress={() =>
                    setModalToShow({
                      type: 'question',
                      questionIndex: item.questionNumber - 1
                    })
                  }
                  style={styles.retryQuestionContainer}
                >
                  <AssetSvg name="zest/rotate-left" height={30} width={30} />
                </TouchableOpacity>
              ) : null}
            </View>
          );
        }}
        ItemSeparatorComponent={() => <View style={styles.ratingSeperator} />}
      />
    );
  };

  return (
    <View style={styles.container}>
      {/* Left column */}
      <View style={[styles.column, styles.columnA]}>
        {/* Close icon */}
        <Button variant="circle" onPress={onExitClicked} style={styles.iconClose}>
          <AssetSvg name="Close" width={24} height={24} />
        </Button>
        {/* Title */}
        <Text variant="WRN700" style={styles.title}>
          {practiceInfo ? translate.quiz.practiceResults() : translate.quiz.results()}
        </Text>
        {/* Quiz Details */}
        <View style={styles.quizDetails}>
          <Text variant="WRN400" style={styles.quizDetailsText} numberOfLines={2}>
            {practiceInfo ? translate.quiz.withPracticeParentheses(quizName) : quizName}
          </Text>
        </View>
        {/* Points */}
        <View style={styles.status}>
          <Text variant="WRN700" style={styles.statusLabel}>
            {translate.quiz.totalPoints()}
          </Text>
          <AssetSvg name={practiceInfo ? 'StarSilver' : 'Star'} width={45} height={45} />
          <Text variant="WRN700" style={styles.statusScore}>
            {practiceInfo ? silverStarsTotalScore : goldStarsTotalScore} / {starsTotalLength}
          </Text>
        </View>
        {/* Actions */}
        <View style={{ rowGap: 24 }}>
          {onTryAgainClicked && (
            // We have a try again callback, so show the button to retry the quiz
            <View style={{ alignItems: 'center' }}>
              <View style={{ zIndex: 2, marginTop: -20, marginBottom: -25.5 }} pointerEvents="none">
                <PngImage
                  source={require('@wre/infinity-question-store/assets/images/TinyLeaningOnEdge.png')}
                  style={{
                    width: 104,
                    height: 136
                  }}
                />
              </View>
              <Button
                onPress={
                  // If we have a practice callback and they didn't get full marks, show the modal to offer practice
                  onPracticeClicked && !isFullMarks
                    ? () => setModalToShow({ type: 'quiz' })
                    : () => onTryAgainClicked()
                }
                style={[styles.button, styles.center]}
                baseColor={colors.seaGreenCrayola}
                onPressInColor={colors.seaGreenCrayolaPressIn}
                testId="TRY_AGAIN_BUTTON"
              >
                <Text variant="ButtonLarge" style={styles.buttonText}>
                  {translate.quiz.modals.tryAgain()}
                </Text>
                <AssetSvg name="ArrowRight" style={styles.iconArrow} width={48} height={49} />
              </Button>
            </View>
          )}

          {onHomeClicked && (
            <View
              testID="BACK_HOME_BUTTON"
              style={{ paddingHorizontal: 32, paddingTop: 22, paddingBottom: 16 }}
            >
              <LinkText action={onHomeClicked} style={[styles.linkText, styles.center]}>
                {translate.quiz.backToHome()}
              </LinkText>
            </View>
          )}
        </View>
      </View>

      {/* Right column */}
      <ScrollView
        horizontal
        scrollEnabled={true}
        contentContainerStyle={{
          flexGrow: 1
        }}
        style={styles.columnBWrapper}
      >
        <View style={[styles.column, styles.columnB]}>{renderRatings()}</View>
      </ScrollView>
      {modalToShow ? renderModal() : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderColor: colors.prussianBlue,
    borderRadius: 24,
    borderWidth: 3,
    display: 'flex',
    flexDirection: 'row',
    height: MINIMUM_QUESTION_HEIGHT - 2 * 16,
    width: QUESTION_WIDTH - 2 * 12,
    marginVertical: 16,
    marginHorizontal: 12
  },
  column: {
    flex: 1,
    paddingHorizontal: 35,
    columnGap: 40,
    height: '100%'
  },
  columnA: {
    borderRightColor: colors.prussianBlue,
    borderRightWidth: 3,
    paddingTop: 64
  },
  columnBWrapper: {
    borderBottomRightRadius: 24,
    borderTopRightRadius: 24,
    flex: 1,
    paddingTop: 16,
    paddingBottom: 16,
    backgroundColor: colors.greyMatter
  },
  columnB: {
    display: 'flex',
    flexDirection: 'row'
  },
  title: {
    paddingBottom: 48,
    textAlign: 'center'
  },
  quizDetails: {
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: 24
  },
  quizDetailsText: { fontSize: 32, textAlign: 'center', lineHeight: 48 },
  status: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingBottom: 48
  },
  statusLabel: {
    paddingRight: 16
  },
  statusScore: {
    paddingLeft: 16
  },
  rating: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    height: 62,
    paddingLeft: 12,
    paddingRight: 12
  },
  ratingSeperator: {
    borderWidth: 1,
    borderColor: colors.grey
  },
  ratingText: {
    textAlign: 'center',
    fontSize: 26,
    width: 77,
    alignSelf: 'stretch',
    lineHeight: 49,
    paddingRight: 12
  },
  ratingIcons: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1
  },
  questionLabel: {
    minWidth: 64
  },
  button: {
    height: 96
  },
  buttonText: {
    paddingRight: 34
  },
  linkText: {
    fontFamily: 'Biotif-Medium',
    fontSize: 29,
    lineHeight: 43.5,
    fontWeight: '500',
    textTransform: 'uppercase'
  },
  icon: {
    paddingBottom: 28
  },
  iconArrow: {
    position: 'absolute',
    right: 24
  },
  iconClose: {
    borderWidth: 0,
    left: 12,
    top: 12,
    position: 'absolute',
    width: 64,
    height: 64,
    zIndex: 100,
    // Override the defaults in button
    paddingTop: 0,
    paddingBottom: 0
  },
  center: {
    alignSelf: 'center'
  },
  retryQuestionContainer: {
    cursor: 'pointer'
  },
  modalContainer: { alignItems: 'center', rowGap: 8, height: 420, width: 708 }
});
